import React, { createContext, useContext, useCallback, useEffect } from 'react';
import { useTranslation as useI18nTranslation } from 'react-i18next';
import axios from 'axios';
import '../i18n/index';
import { API } from '../lib/axiosClient';

const LanguageContext = createContext();

export const SUPPORTED_LANGUAGES = [
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'cs', label: 'Čeština', flag: '🇨🇿' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'ro', label: 'Română', flag: '🇷🇴' },
  { code: 'hu', label: 'Magyar', flag: '🇭🇺' },
];

export const LanguageProvider = ({ children }) => {
  const { t, i18n } = useI18nTranslation();
  const language = i18n.language?.substring(0, 2) || 'de';

  // Bug-Fix P2-4 (2026-05-27): Initial-Sprach-Poll wartet auf
  // ``qm8d:auth:ready`` Event aus AuthContext, statt blind beim Mount
  // ``/auth/language`` aufzurufen und 401-Spam in der Browser-Console
  // beim Login-Screen zu erzeugen.
  useEffect(() => {
    const loadLanguageFromDB = () => {
      axios.get(`${API}/auth/language`, { _skipAuthRetry: true }).then(res => {
        const dbLang = res.data?.language;
        if (dbLang && dbLang !== language) {
          i18n.changeLanguage(dbLang);
          localStorage.setItem('qm8d_language', dbLang);
          document.documentElement.lang = dbLang;
        }
      }).catch(() => {});
    };

    window.addEventListener('qm8d:auth:ready', loadLanguageFromDB);
    return () => window.removeEventListener('qm8d:auth:ready', loadLanguageFromDB);
  }, []);

  const changeLanguage = useCallback(async (lang) => {
    await i18n.changeLanguage(lang);
    localStorage.setItem('qm8d_language', lang);
    document.documentElement.lang = lang;
    // In DB speichern (Cookie-Auth)
    axios.put(`${API}/auth/language`, { language: lang }, { _skipAuthRetry: true }).catch(() => {});
  }, [i18n]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, i18n }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
